import {
  ArrowClockwise,
  ArrowSquareOut,
  Copy,
  DotsThreeVertical,
  Funnel,
  Hash,
  Info,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Trash,
  WarningCircle,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Input } from '@/components/ui-kit/input';
import { useToast } from '@/hooks/use-toast';
import { copyTextToClipboard } from '@/lib/clipboard';
import { cn } from '@/lib/utils';
import { ContactsPagination } from '@/modules/contacts/components/contacts-pagination/contacts-pagination';
import { SegmentDeleteDialog } from './segment-delete-dialog';
import { SegmentMemberCell } from './segment-member-cell';
import { SegmentRenameModal } from './segment-rename-modal';
import {
  useCreateSegment,
  useGetSegments,
  usePreviewSavedSegment,
} from '../hooks/use-segments';
import { getSegmentById } from '../services/segments.service';
import type { Segment } from '../types/segments.types';
import { getSegmentSessionGuidance } from '../utils/segment-api-errors';
import {
  buildDuplicateSegmentName,
  formatSegmentDate,
  formatSegmentsSubtitle,
  getSegmentUpdatedDate,
} from '../utils/segments-format';

const tableHeadClassName =
  'sticky top-0 z-10 whitespace-nowrap bg-neutral-50 px-4 py-3 text-left text-[13px] font-semibold leading-[18px] text-neutral-600';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

type SegmentsOverviewProps = {
  onCreate: () => void;
  onEdit: (segmentId: string) => void;
  clearSearchRequest?: number;
};

export const SegmentsOverview = ({
  onCreate,
  onEdit,
  clearSearchRequest = 0,
}: SegmentsOverviewProps) => {
  const { toast } = useToast();
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [segmentToRename, setSegmentToRename] = useState<Segment | null>(null);
  const [segmentToDelete, setSegmentToDelete] = useState<Segment | null>(null);
  const [refreshingSegmentId, setRefreshingSegmentId] = useState<string | null>(null);

  const { mutateAsync: createSegment, isPending: isDuplicating } = useCreateSegment();
  const { mutateAsync: previewSavedSegment, isPending: isRefreshing } = usePreviewSavedSegment();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPageNo(1);
  }, [debouncedSearch, pageSize]);

  useEffect(() => {
    if (clearSearchRequest === 0) return;
    setSearch('');
    setDebouncedSearch('');
  }, [clearSearchRequest]);

  const queryParams = useMemo(
    () => ({
      pageNo,
      pageSize,
      q: debouncedSearch || undefined,
      sort: 'updated' as const,
      order: 'desc' as const,
    }),
    [debouncedSearch, pageNo, pageSize]
  );

  const { data, isLoading, error, refetch } = useGetSegments(queryParams);

  const segments = useMemo(() => data?.items ?? [], [data?.items]);
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const hasNextPage = Boolean(data?.hasNextPage);
  const hasPreviousPage = Boolean(data?.hasPreviousPage);
  const isError = Boolean(error);
  const showLoading = isLoading && segments.length === 0;
  const hasSearchQuery = debouncedSearch.length > 0;
  const showEmpty = !showLoading && !isError && totalCount === 0 && !hasSearchQuery;
  const showNoResults = !showLoading && !isError && totalCount === 0 && hasSearchQuery;

  useEffect(() => {
    if (totalPages > 0 && pageNo > totalPages) {
      setPageNo(totalPages);
    }
  }, [pageNo, totalPages]);

  const subtitle = formatSegmentsSubtitle({
    isLoading: showLoading,
    isError,
    totalCount,
    searchQuery: hasSearchQuery ? debouncedSearch : undefined,
  });

  const handleClearSearch = () => {
    setSearch('');
    setDebouncedSearch('');
  };

  const existingSegmentNames = useMemo(
    () => segments.map((segment) => segment.name.trim()).filter(Boolean),
    [segments]
  );

  const handleDuplicateSegment = async (segment: Segment) => {
    if (isDuplicating) return;

    const duplicateName = buildDuplicateSegmentName(segment.name, existingSegmentNames);

    try {
      const source = await getSegmentById(segment.itemId);
      if (!source.rules) {
        throw new Error('Segment rules are unavailable.');
      }
      await createSegment({
        name: duplicateName,
        description: source.description?.trim() || undefined,
        rules: source.rules,
      });
      toast({
        variant: 'success',
        title: 'Segment duplicated',
        description: `Duplicated as "${duplicateName}".`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to duplicate segment',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleCopySegmentId = async (segment: Segment) => {
    const copied = await copyTextToClipboard(segment.itemId);
    if (copied) {
      toast({
        variant: 'success',
        title: 'Segment ID copied',
      });
      return;
    }

    toast({
      variant: 'destructive',
      title: 'Could not copy segment ID',
    });
  };

  const handleRefreshMemberCount = async (segment: Segment) => {
    if (isRefreshing) return;
    setRefreshingSegmentId(segment.itemId);
    try {
      await previewSavedSegment(segment.itemId);
      toast({
        variant: 'success',
        title: 'Segment count refreshed',
        description: `${segment.name} member count updated just now.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to refresh segment count',
        description:
          getSegmentSessionGuidance(error) ??
          (error instanceof Error ? error.message : 'Please try again.'),
      });
    } finally {
      setRefreshingSegmentId(null);
    }
  };

  const renderActionsMenu = (segment: Segment) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex size-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
          title="Segment actions"
          aria-label={`Actions for ${segment.name || 'segment'}`}
          onClick={(event) => event.stopPropagation()}
        >
          <DotsThreeVertical className="size-[18px]" weight="bold" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => onEdit(segment.itemId)}>
          <ArrowSquareOut className="size-4" aria-hidden />
          Open in builder
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setSegmentToRename(segment)}>
          <PencilSimple className="size-4" aria-hidden />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isDuplicating}
          onClick={() => void handleDuplicateSegment(segment)}
        >
          <Copy className="size-4" aria-hidden />
          Duplicate
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isRefreshing}
          onClick={() => void handleRefreshMemberCount(segment)}
        >
          <ArrowClockwise className="size-4" aria-hidden />
          Refresh count
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void handleCopySegmentId(segment)}>
          <Hash className="size-4" aria-hidden />
          Copy ID
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-error focus:text-error"
          onClick={() => setSegmentToDelete(segment)}
        >
          <Trash className="size-4" aria-hidden />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-5">
        <div className="flex items-start gap-2.5 rounded-lg border border-primary-100 bg-primary-50 px-3.5 py-3">
          <Info className="mt-0.5 size-4 shrink-0 text-primary-600" aria-hidden />
          <p className="text-[13px] leading-[18px] text-primary-900">
            Dynamic, rule-based audiences live here. Fixed, marketer-curated audiences are on the{' '}
            <Link to="/lists" className="font-medium underline">
              Lists
            </Link>{' '}
            page instead — segment counts below are estimates, not live membership rows.
          </p>
        </div>

        <div className="flex flex-wrap items-start gap-4">
          <div>
            <h1 className="text-[28px] font-semibold leading-9 text-neutral-900">Segments</h1>
            <p className="mt-0.5 text-sm leading-5 text-neutral-600">{subtitle}</p>
          </div>
          <div className="flex-1" />
          <Button
            type="button"
            className="h-10 bg-primary-600 px-4 text-white shadow-e1 hover:bg-primary-700"
            onClick={onCreate}
          >
            <Plus className="size-4" aria-hidden />
            New segment
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-[220px] max-w-[360px] flex-1 items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-500 focus-within:border-primary-600 focus-within:ring-2 focus-within:ring-primary-600/30">
            <MagnifyingGlass className="size-4 shrink-0" aria-hidden />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search segments by name"
              aria-label="Search segments by name"
              className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0"
            />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-e1">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full min-w-[780px] border-collapse text-sm leading-5">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50">
                  <th className={tableHeadClassName}>Name</th>
                  <th className={tableHeadClassName}>Description</th>
                  <th className={tableHeadClassName}>Members</th>
                  <th className={tableHeadClassName}>Updated</th>
                  <th className={cn(tableHeadClassName, 'w-14')} />
                </tr>
              </thead>
              <tbody>
                {showLoading
                  ? Array.from({ length: 3 }).map((_, index) => (
                      <tr key={`skeleton-${index}`} className="border-b border-neutral-100">
                        <td className="px-4 py-3">
                          <span className="block h-4 w-36 animate-pulse rounded bg-neutral-200" />
                        </td>
                        <td className="px-4 py-3">
                          <span className="block h-4 w-52 animate-pulse rounded bg-neutral-200" />
                        </td>
                        <td className="px-4 py-3">
                          <span className="block h-4 w-24 animate-pulse rounded bg-neutral-200" />
                        </td>
                        <td className="px-4 py-3">
                          <span className="block h-4 w-24 animate-pulse rounded bg-neutral-200" />
                        </td>
                        <td className="px-4 py-3" />
                      </tr>
                    ))
                  : null}

                {!showLoading && isError ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-14 text-center">
                      <div className="mx-auto flex max-w-[360px] flex-col items-center gap-2">
                        <WarningCircle className="size-7 text-error" aria-hidden />
                        <p className="font-medium text-neutral-900">Could not load segments</p>
                        <p className="text-[13px] leading-[18px] text-neutral-500">
                          {getSegmentSessionGuidance(error) ??
                            'Something went wrong on our end. Try again.'}
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          className="mt-1 h-8 border-neutral-200"
                          onClick={() => void refetch()}
                        >
                          <ArrowClockwise className="size-4" aria-hidden />
                          Retry
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {showEmpty ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-14 text-center">
                      <div className="mx-auto flex max-w-[360px] flex-col items-center gap-2">
                        <Funnel className="size-7 text-neutral-400" aria-hidden />
                        <p className="font-medium text-neutral-900">No segments yet</p>
                        <p className="text-[13px] leading-[18px] text-neutral-500">
                          Build a rule-based segment to target contacts by attribute or behavior —
                          it stays up to date on its own.
                        </p>
                        <Button
                          type="button"
                          className="mt-1 h-8 bg-primary-600 px-3 text-white hover:bg-primary-700"
                          onClick={onCreate}
                        >
                          <Plus className="size-3.5" aria-hidden />
                          New segment
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {showNoResults ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-14 text-center">
                      <div className="mx-auto flex max-w-[360px] flex-col items-center gap-2">
                        <MagnifyingGlass className="size-7 text-neutral-400" aria-hidden />
                        <p className="font-medium text-neutral-900">
                          No segments match &ldquo;{debouncedSearch}&rdquo;
                        </p>
                        <p className="text-[13px] leading-[18px] text-neutral-500">
                          Try a different name, or clear your search.
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          className="mt-1 h-8 border-neutral-200"
                          onClick={handleClearSearch}
                        >
                          Clear search
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {!showLoading &&
                  !isError &&
                  segments.map((segment) => (
                    <tr
                      key={segment.itemId}
                      className="data-row cursor-pointer border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50"
                      onClick={() => onEdit(segment.itemId)}
                    >
                      <td className="px-4 py-3 align-middle font-medium text-neutral-900">
                        {segment.name.trim() || 'Untitled segment'}
                      </td>
                      <td className="max-w-[320px] px-4 py-3 align-middle text-neutral-600">
                        <div className="truncate">
                          {segment.description?.trim() ? (
                            segment.description
                          ) : (
                            <span className="text-neutral-400">No description</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <SegmentMemberCell
                          matchCount={segment.matchCount}
                          matchCountAt={segment.matchCountAt}
                          isRefreshing={refreshingSegmentId === segment.itemId}
                          onRefresh={() => void handleRefreshMemberCount(segment)}
                        />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-middle text-neutral-600">
                        {formatSegmentDate(getSegmentUpdatedDate(segment))}
                      </td>
                      <td
                        className="px-4 py-3 align-middle text-center"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {renderActionsMenu(segment)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {!showEmpty && !showNoResults ? (
            <ContactsPagination
              variant="simple"
              pageNo={pageNo}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onPageChange={setPageNo}
              onPageSizeChange={setPageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              itemLabel="segments"
              isLoading={showLoading}
              isError={isError}
            />
          ) : null}
        </div>
      </div>

      <SegmentRenameModal
        open={Boolean(segmentToRename)}
        onOpenChange={(open) => {
          if (!open) setSegmentToRename(null);
        }}
        segment={segmentToRename}
      />

      <SegmentDeleteDialog
        open={Boolean(segmentToDelete)}
        onOpenChange={(open) => {
          if (!open) setSegmentToDelete(null);
        }}
        segment={segmentToDelete}
      />
    </>
  );
};
